/**
 * URL API SERVER
 */
import { API_URL } from '@/App.settings';

/**
 * Axios
 */
import axios from 'axios';

/**
 * Composants VUE et VUEX
 */
import { Vue } from 'vue-property-decorator';
import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';

/**
 * Traduction
 */
import { i18n } from '@/main';

/**
 * Statut d'envoi d'un fichier affiché dans la fenêtre d'upload
 */
export type UploadFileStatus = "Pending" | "Uploading" | "Sent" | "Failed";

/**
 * Fichier affiché dans la fenêtre d'upload
 */
export interface UploadFileItem {
    Name: string;
    Size: number;
    Status: UploadFileStatus;
    File: File;
}

/**
 * Résultat d'un upload
 */
export interface UploadResult {
    success: boolean;
    message?: string;
}

/**
 * Taille maximale d'un lot d'upload de fichiers (octets).
 * Chaque lot reste sous la limite de taille de requête du serveur (maxRequestLength, 4 Mo par défaut),
 * avec une marge pour l'overhead multipart et les champs de formulaire additionnels.
 */
const MAX_UPLOAD_BATCH_SIZE = 3 * 1024 * 1024;

/**
 * File d'attente de l'upload (références File nécessaires à l'orchestration).
 * Les objets sont partagés avec l'état VueX pour l'affichage de la fenêtre d'upload.
 */
let uploadQueue: UploadFileItem[] = [];
let uploadEndpoint: string = "";
let uploadArgs: Record<string, any> = {};
let uploadOnBatchSuccess: ((rawResult: any) => void) | null = null;
let uploadBusy: boolean = false;

/**
 * Resolver de la fermeture manuelle de la fenêtre d'upload :
 * l'envoi ne rend la main à l'appelant que lorsque l'utilisateur ferme la fenêtre.
 */
let uploadDialogResolver: (() => void) | null = null;

/**
 * Découpage d'une liste de fichiers en lots ne dépassant pas la taille maximale d'une requête
 * @param files Fichiers à uploader
 * @returns Lots de fichiers
 */
const buildFileBatches = (files: File[]): File[][] => {
    const batches: File[][] = [];
    let batch: File[] = [];
    let size = 0;

    files.forEach(file => {
        if (batch.length > 0 && size + file.size > MAX_UPLOAD_BATCH_SIZE) {
            batches.push(batch);
            batch = [];
            size = 0;
        }
        batch.push(file);
        size += file.size;
    });

    if (batch.length > 0) {
        batches.push(batch);
    }

    return batches;
};

/**
 * STORE POUR LA GESTION GENERIQUE DE L'UPLOAD DE FICHIERS AVEC FENETRE DE PROGRESSION.
 * Utilisable depuis n'importe quel module : l'endpoint et les champs de formulaire
 * sont fournis par l'appelant, la fenêtre est montée au niveau de l'application.
 */
@Module({ namespaced: true })
class UploadStore extends VuexModule {

    // #region State
    private Files: UploadFileItem[] = [];
    private DialogVisible: boolean = false;
    private Progress: number = 0;
    private InProgress: boolean = false;
    // #endregion

    // #region Getters
    public get GetFiles() { return this.Files; }
    public get GetDialogVisible() { return this.DialogVisible; }
    public get GetProgress() { return this.Progress; }
    public get GetInProgress() { return this.InProgress; }

    /**
     * Index du fichier suivi par la fenêtre d'upload (premier fichier en cours, sinon premier échec)
     */
    public get GetCurrentIndex(): number {
        const uploading = this.Files.findIndex(item => item.Status === "Uploading");
        return uploading >= 0 ? uploading : this.Files.findIndex(item => item.Status === "Failed");
    }
    // #endregion

    // #region Mutations
    @Mutation
    private initFiles(items: UploadFileItem[]) {
        this.Files = items;
    }

    @Mutation
    private setFileStatus({ index, status }: { index: number, status: UploadFileStatus }) {
        if (this.Files[index]) {
            this.Files[index].Status = status;
        }
    }

    @Mutation
    private setDialogVisible(value: boolean) {
        this.DialogVisible = value;
    }

    @Mutation
    private setProgress(value: number) {
        this.Progress = value;
    }

    @Mutation
    private setInProgress(value: boolean) {
        this.InProgress = value;
    }
    // #endregion

    // #region Actions

    /**
     * Upload d'une liste de fichiers vers un endpoint multipart, avec la fenêtre de progression :
     * barre de progression globale et liste des fichiers avec leur statut (fenêtre non fermable pendant l'upload).
     * L'action ne rend la main que lorsque l'utilisateur ferme la fenêtre.
     * @param param0 Endpoint : URL de l'API recevant les fichiers (champ "Resources"),
     *               Args : champs de formulaire additionnels (ex : entité liée sérialisée),
     *               Files : fichiers à uploader,
     *               OnBatchSuccess : callback appelé avec la réponse de chaque lot réussi
     * @returns UploadResult (Success, message)
     */
    @Action({ rawError: true })
    public async UploadFiles({ Endpoint, Args, Files, OnBatchSuccess }: { Endpoint: string, Args?: Record<string, any>, Files: File[], OnBatchSuccess?: (rawResult: any) => void }): Promise<UploadResult> {
        // La fenêtre d'upload remplace le masque de chargement
        this.context.dispatch("HideLoadingMask", {}, { root: true });

        uploadEndpoint = Endpoint;
        uploadArgs = Args ?? {};
        uploadOnBatchSuccess = OnBatchSuccess ?? null;
        uploadQueue = Files.map(file => ({ Name: file.name, Size: file.size, Status: "Pending" as UploadFileStatus, File: file }));

        this.context.commit('initFiles', uploadQueue);
        this.context.commit('setProgress', 0);
        this.context.commit('setDialogVisible', true);

        return await this.context.dispatch('uploadPendingFiles');
    }

    /**
     * Upload par lots de tous les fichiers en attente ou en échec.
     * Chaque lot reste sous la taille maximale d'une requête serveur.
     * @returns UploadResult (Success, message)
     */
    @Action({ rawError: true })
    private async uploadPendingFiles(): Promise<UploadResult> {
        if (uploadBusy) {
            return { success: false };
        }

        uploadBusy = true;
        this.context.commit('setInProgress', true);

        const pending = uploadQueue
            .map((item, index) => ({ Index: index, Item: item }))
            .filter(entry => entry.Item.Status === "Pending" || entry.Item.Status === "Failed")
            .map(entry => ({ Index: entry.Index, File: entry.Item.File }));

        const totalBytes = uploadQueue.reduce((sum, item) => sum + item.Size, 0);
        let sentBytes = uploadQueue.filter(item => item.Status === "Sent").reduce((sum, item) => sum + item.Size, 0);

        this.context.commit('setProgress', totalBytes > 0 ? Math.round((sentBytes / totalBytes) * 100) : 0);

        const batches = buildFileBatches(pending.map(item => item.File));
        const fileIndexes = new Map<File, number>();
        pending.forEach(item => fileIndexes.set(item.File, item.Index));

        try {
            for (const batch of batches) {
                // Statut "en cours" pour les fichiers du lot
                batch.forEach(file => this.context.commit('setFileStatus', { index: fileIndexes.get(file), status: "Uploading" }));

                const result = await this.context.dispatch('uploadBatch', batch) as UploadResult;

                if (result && result.success) {
                    batch.forEach(file => this.context.commit('setFileStatus', { index: fileIndexes.get(file), status: "Sent" }));

                    sentBytes += batch.reduce((sum, file) => sum + file.size, 0);
                    this.context.commit('setProgress', totalBytes > 0 ? Math.round((sentBytes / totalBytes) * 100) : 0);
                } else {
                    batch.forEach(file => this.context.commit('setFileStatus', { index: fileIndexes.get(file), status: "Failed" }));
                }
            }
        } finally {
            uploadBusy = false;
            this.context.commit('setInProgress', false);
        }

        await new Promise<void>(resolve => { uploadDialogResolver = resolve; });

        return uploadQueue.every(item => item.Status === "Sent")
            ? { success: true }
            : { success: false, message: i18n.t("UploadFailedRetry").toString() };
    }

    /**
     * Upload d'un lot de fichiers vers l'endpoint configuré (multipart, champ "Resources")
     * @param files Lot de fichiers
     * @returns UploadResult (Success, message)
     */
    @Action({ rawError: true })
    private async uploadBatch(files: File[]): Promise<UploadResult> {
        try {
            const formData = new FormData();

            files.forEach((file) => {
                formData.append("Resources", file);
            });

            const args = {
                Token: (Vue.cookies.get('access_token') as string) || "",
                ...uploadArgs
            };

            Object.keys(args).forEach(key => {
                if (args[key] !== undefined && args[key] !== null) {
                    formData.append(key, args[key]);
                }
            });

            const response = await axios.post(uploadEndpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (uploadOnBatchSuccess) {
                uploadOnBatchSuccess(response.data["result"]);
            }

            return { success: true };
        } catch (e: any) {
            return { success: false, message: e.response?.data?.Message || e.message };
        }
    }

    /**
     * Nouvelle tentative d'envoi d'un fichier en échec (bouton "Réessayer" de la fenêtre d'upload)
     * @param param0 Index : index du fichier dans la liste
     */
    @Action({ rawError: true })
    public async RetryUploadFile({ Index: index }: { Index: number }): Promise<void> {
        const item = uploadQueue[index];

        if (uploadBusy || !item || item.Status !== "Failed") {
            return;
        }

        uploadBusy = true;
        this.context.commit('setInProgress', true);
        this.context.commit('setFileStatus', { index, status: "Uploading" });

        const totalBytes = uploadQueue.reduce((sum, f) => sum + f.Size, 0);
        const sentBytes = uploadQueue.filter(f => f.Status === "Sent").reduce((sum, f) => sum + f.Size, 0);

        try {
            const result = await this.context.dispatch('uploadBatch', [item.File]) as UploadResult;

            this.context.commit('setFileStatus', { index, status: result && result.success ? "Sent" : "Failed" });
        } catch (e) {
            this.context.commit('setFileStatus', { index, status: "Failed" });
        } finally {
            uploadBusy = false;
            this.context.commit('setInProgress', false);
        }

        const newSentBytes = uploadQueue.filter(f => f.Status === "Sent").reduce((sum, f) => sum + f.Size, 0);
        this.context.commit('setProgress', totalBytes > 0 ? Math.round((newSentBytes / totalBytes) * 100) : 0);
    }

    /**
     * Fermeture de la fenêtre d'upload (uniquement possible hors upload en cours)
     */
    @Action({ rawError: true })
    public CloseUploadDialog(): void {
        if (!uploadBusy) {
            this.context.commit('setDialogVisible', false);

            if (uploadDialogResolver) {
                const resolve = uploadDialogResolver;
                uploadDialogResolver = null;
                resolve();
            }
        }
    }

    // #endregion
}

export default UploadStore;
